import React, { useEffect, useRef } from "react";
import styled from "styled-components";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import { formatCurrency } from "../config/market";
import { deliveryOfferLabel, deliveryOfferEventKey } from "../utils/deliveryOffers";
import {
  trackDeliveryEstimateView,
  trackDeliveryEstimateUnavailable,
} from "../utils/analytics";

/**
 * CartSummary: the money of ONE vendor's cart, the same way on every surface
 * that shows it (the restaurant, pharmacy, brand and shop drawers, the in-house
 * cart and every block of the cart hub):
 *
 *   Subtotal   the goods, as the server priced them
 *   Delivery   the fee, any free delivery, and when it arrives
 *   Total      the server's total, the loudest figure
 *
 * Renders an `estimate` exactly as the server answered it: one vendor's from
 * hooks/useDeliveryEstimate, or one hub block's from hooks/useCartQuote. The
 * subtotal is the server's once it has priced these lines; `subtotal` (the
 * cart's own sum) stands in only until then. Nothing is added up here, and
 * nothing here ever covers more than one vendor: each cart is its own order.
 *
 * The delivery row says nothing when no drop-off is set or the vendor does not
 * deliver there: CoverageBanner, beside every cart, already asks for the area
 * or explains the range. `compact` shows the subtotal alone (a hub block whose
 * cart is reviewed on its storefront).
 */
const CartSummary = ({
  estimate,
  subtotal = 0,
  vendorId,
  vendorType,
  surface = "cart",
  compact = false,
  format = formatCurrency,
  className,
  style,
}) => {
  const status = estimate?.status || "disabled";
  const { fee, minutes, offer, total, freight, goodsPriced, itemsSubtotal } = estimate || {};
  const goods = goodsPriced ? itemsSubtotal : Number(subtotal) || 0;

  /* One event per vendor and outcome, so a cart re-render is not a new view. */
  const lastEventKey = useRef("");
  useEffect(() => {
    if (compact || status === "disabled" || status === "loading") return;
    const eventKey = `${vendorId}:${status}:${fee}:${offer?.kind || ""}`;
    if (eventKey === lastEventKey.current) return;
    lastEventKey.current = eventKey;
    if (status === "ready") {
      trackDeliveryEstimateView({
        surface,
        vendorType,
        fee,
        waivedAmount: offer?.waivedAmount,
        offer: deliveryOfferEventKey(offer),
      });
    } else {
      trackDeliveryEstimateUnavailable({ surface, vendorType, reason: status });
    }
  }, [compact, vendorId, status, fee, offer, surface, vendorType]);

  const showDelivery = !compact && !["disabled", "needs_location", "out_of_range"].includes(status);
  // The server's total, once it has priced something to total up. A freight
  // leg's total is the goods alone (its fee is quoted later), so it is not a
  // total with delivery and is not shown.
  const showTotal =
    showDelivery && status === "ready" && !freight && total !== null && total !== undefined && Number(itemsSubtotal) > 0;

  return (
    <Summary role="status" aria-live="polite" className={className} style={style} $compact={compact}>
      <Row data-row="subtotal">
        <span>Subtotal</span>
        <Value>{format(goods)}</Value>
      </Row>
      {showDelivery && (
        <>
          <Row>
            <Label>
              <LocalShippingOutlinedIcon fontSize="small" aria-hidden="true" />
              Delivery
            </Label>
            <Value>
              {status === "loading" && <Muted>Working it out</Muted>}
              {status === "needs_sign_in" && <Muted>Shown after you sign in</Muted>}
              {status === "error" && (
                <Retry type="button" onClick={estimate?.refresh}>
                  Could not load. Try again
                </Retry>
              )}
              {status === "ready" && freight && <Muted>Quoted separately</Muted>}
              {status === "ready" && !freight && offer && (
                <>
                  <Struck>{format(offer.originalFee)}</Struck> {fee > 0 ? format(fee) : "Free"}
                </>
              )}
              {status === "ready" && !freight && !offer && format(fee)}
            </Value>
          </Row>
          {status === "ready" && (offer || minutes > 0) && (
            <Note>
              {offer ? deliveryOfferLabel(offer) : null}
              {offer && minutes > 0 ? ". " : null}
              {minutes > 0 ? `Arrives in about ${minutes} min` : null}
            </Note>
          )}
        </>
      )}
      {showTotal && (
        <TotalRow data-row="total">
          <span>Total</span>
          <TotalValue>{format(total)}</TotalValue>
        </TotalRow>
      )}
    </Summary>
  );
};

const Summary = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: ${(p) => (p.$compact ? "8px 12px" : "10px 12px")};
  margin: 6px 0;
  border-radius: 10px;
  border: 1px solid #e1e6de;
  background: #f7f9f5;
  color: #15232a;
  font-size: 14px;
  line-height: 1.4;
  text-align: left;
  font-variant-numeric: tabular-nums;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-weight: 500;
`;

const Label = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  svg {
    color: #24525f;
  }
`;

const Value = styled.span`
  font-weight: 700;
  white-space: nowrap;
`;

const Muted = styled.span`
  color: #4b5a60;
  font-weight: 500;
`;

const Struck = styled.s`
  color: #4b5a60;
  font-weight: 500;
`;

const Note = styled.span`
  color: #2c4d08;
  font-size: 13px;
`;

const TotalRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  border-top: 1px solid #e1e6de;
  padding-top: 8px;
  margin-top: 4px;
  font-size: 15px;
  font-weight: 700;
`;

const TotalValue = styled.span`
  font-size: 22px;
  font-weight: 800;
  letter-spacing: -0.01em;
  white-space: nowrap;
  color: var(--cart-total-ink, #10262b);
`;

const Retry = styled.button`
  border: none;
  background: none;
  padding: 0;
  color: #24525f;
  font: inherit;
  font-weight: 700;
  text-decoration: underline;
  cursor: pointer;
`;

export default CartSummary;
